import { useEffect, useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { getPresentationData, isValidCode } from '../utils/presentationLinks';
import { FileText, Search, BarChart3, Zap, MessageCircle } from 'lucide-react';
import Footer from '../components/Footer';

interface ApresentacaoParams {
  nome: string;
  escritorio: string;
  contato: string;
  precoPix: string;
  precoParcelado: string;
  parcelas: string;
  mensalidade: string;
}

export default function Apresentacao() {
  const [searchParams] = useSearchParams();
  const { code } = useParams<{ code: string }>();
  const [params, setParams] = useState<ApresentacaoParams>({
    nome: '',
    escritorio: '',
    contato: '',
    precoPix: '22700',
    precoParcelado: '26500',
    parcelas: '15',
    mensalidade: 'Dependerá do uso e da complexidade das ferramentas usadas'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPresentationData = () => {
      setLoading(true);
      setError(null);

      if (code) {
        // Usar sistema de links curtos
        if (!isValidCode(code)) {
          setError('Link de apresentação inválido ou expirado');
          setLoading(false);
          return;
        }

        const data = getPresentationData(code);
        if (data) {
          setParams({
            nome: data.nome,
            escritorio: data.escritorio,
            contato: data.contato,
            precoPix: data.precoPix,
            precoParcelado: data.precoParcelado,
            parcelas: data.parcelas,
            mensalidade: data.mensalidade
          });
        } else {
          setError('Dados da apresentação não encontrados');
        }
      } else {
        // Fallback para sistema antigo (URLs com parâmetros)
        const urlParams = {
          nome: searchParams.get('nome') || '',
          escritorio: searchParams.get('escritorio') || '',
          contato: searchParams.get('contato') || '',
          precoPix: searchParams.get('preco_pix') || '22700',
          precoParcelado: searchParams.get('preco_parcelado') || '26500',
          parcelas: searchParams.get('parcelas') || '15',
          mensalidade: searchParams.get('mensalidade') || 'Dependerá do uso e da complexidade das ferramentas usadas'
        };
        
        setParams(urlParams);
      }

      setLoading(false);
    };

    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    loadPresentationData();
  }, [searchParams, code]);

  const formatCurrency = (value: string) => {
    const numValue = parseFloat(value.replace(/\D/g, ''));
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numValue / 100);
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    // Remove todos os caracteres não numéricos
    const cleaned = phone.replace(/\D/g, '');
    // Formata como (XX) XXXXX-XXXX
    if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    }
    // Formata como (XX) XXXX-XXXX
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#081428] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-lg">Carregando apresentação...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#081428] text-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold mb-4">Link Inválido</h1>
          <p className="text-gray-300 mb-6">{error}</p>
          <a 
            href="/apresentacao/config" 
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300"
          >
            Criar Nova Apresentação
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#081428] text-white">
      
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12 group">
            <h1 className="text-3xl md:text-4xl font-light mb-4 text-white group-hover:text-blue-100 transition-colors duration-500">
              O Escritório Inteligente Chegou
            </h1>
            <p className="text-lg text-gray-400 mb-2 group-hover:text-gray-300 transition-colors duration-500">
              A nova era da advocacia não é apenas digital.
            </p>
            <p className="text-lg text-blue-400 font-medium group-hover:text-blue-300 group-hover:scale-105 transition-all duration-500 inline-block">
              É inteligente.
            </p>
            {params.escritorio && (
              <div className="mt-6 p-4 bg-white/5 border border-white/10 rounded-lg max-w-lg mx-auto group-hover:bg-white/10 group-hover:border-blue-400/30 group-hover:shadow-lg group-hover:shadow-blue-500/20 transition-all duration-500">
                <p className="text-sm text-gray-300 group-hover:text-white transition-colors duration-300">
                  Apresentação personalizada para <span className="text-blue-400 font-medium group-hover:text-blue-300 transition-colors duration-300">{params.escritorio}</span>
                </p>
              </div>
            )}
          </div>

          <div className="space-y-12">
            {/* O que é um Escritório inteligente? */}
            <section className="group/section hover:bg-white/5 rounded-2xl p-8 transition-all duration-500 border border-transparent hover:border-blue-500/20">
              <h2 className="text-2xl font-light mb-6 text-blue-400 group-hover/section:text-blue-300 transition-colors duration-500">
                <span className="inline-block group-hover/section:scale-105 transition-transform duration-300">🤖</span> O que é um Escritório inteligente?
            </h2>
              <p className="text-gray-300 mb-8 leading-relaxed group-hover/section:text-gray-200 transition-colors duration-500">
                É um escritório que utiliza avançados sistemas de IA para o máximo de tarefas, como por exemplo:
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center space-x-6 py-6 group/feature hover:bg-white/10 rounded-xl transition-all duration-300 border border-transparent hover:border-blue-500/30 hover:shadow-lg hover:shadow-blue-500/20">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 rounded-xl flex items-center justify-center flex-shrink-0 group-hover/feature:scale-110 group-hover/feature:rotate-3 transition-all duration-300 shadow-lg group-hover/feature:shadow-blue-500/40">
                    <FileText className="w-8 h-8 text-white group-hover/feature:scale-110 transition-transform duration-300" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white mb-3 group-hover/feature:text-blue-100 transition-colors duration-300">Geração de documentos</h3>
                    <p className="text-sm text-gray-400 mb-4 group-hover/feature:text-gray-300 transition-colors duration-300 leading-relaxed">
                      A elegante alquimia de dados brutos transformando-se em narrativas perfeitamente estruturadas. Cada palavra posicionada com precisão, cada formato renderizado com perfeição absoluta.
                    </p>
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse group-hover/feature:bg-blue-300 transition-colors duration-300"></div>
                      <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse group-hover/feature:bg-blue-300 transition-colors duration-300" style={{animationDelay: '0.2s'}}></div>
                      <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse group-hover/feature:bg-blue-300 transition-colors duration-300" style={{animationDelay: '0.4s'}}></div>
                      <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse group-hover/feature:bg-blue-300 transition-colors duration-300" style={{animationDelay: '0.6s'}}></div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-6 py-6 group/feature hover:bg-white/10 rounded-xl transition-all duration-300 border border-transparent hover:border-green-500/30 hover:shadow-lg hover:shadow-green-500/20">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 via-green-600 to-green-700 rounded-xl flex items-center justify-center flex-shrink-0 group-hover/feature:scale-110 group-hover/feature:rotate-3 transition-all duration-300 shadow-lg group-hover/feature:shadow-green-500/40">
                    <Search className="w-8 h-8 text-white group-hover/feature:scale-110 transition-transform duration-300" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white mb-3 group-hover/feature:text-green-100 transition-colors duration-300">Análise profunda</h3>
                    <p className="text-sm text-gray-400 mb-4 group-hover/feature:text-gray-300 transition-colors duration-300 leading-relaxed">
                      A arte sedutora da penetração intelectual. Não é apenas olhar, é ver além da superfície, dissecando camadas de informação com precisão cirúrgica para desenterrar padrões ocultos e verdades profundas.
                    </p>
                    <div className="flex space-x-1 items-end">
                      <div className="w-2 h-4 bg-green-400 rounded-full animate-pulse group-hover/feature:bg-green-300 transition-colors duration-300"></div>
                      <div className="w-2 h-8 bg-green-400 rounded-full animate-pulse group-hover/feature:bg-green-300 transition-colors duration-300" style={{animationDelay: '0.1s'}}></div>
                      <div className="w-2 h-3 bg-green-400 rounded-full animate-pulse group-hover/feature:bg-green-300 transition-colors duration-300" style={{animationDelay: '0.2s'}}></div>
                      <div className="w-2 h-6 bg-green-400 rounded-full animate-pulse group-hover/feature:bg-green-300 transition-colors duration-300" style={{animationDelay: '0.3s'}}></div>
                      <div className="w-2 h-5 bg-green-400 rounded-full animate-pulse group-hover/feature:bg-green-300 transition-colors duration-300" style={{animationDelay: '0.4s'}}></div>
                      <div className="w-2 h-7 bg-green-400 rounded-full animate-pulse group-hover/feature:bg-green-300 transition-colors duration-300" style={{animationDelay: '0.5s'}}></div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-6 py-6 group/feature hover:bg-white/10 rounded-xl transition-all duration-300 border border-transparent hover:border-purple-500/30 hover:shadow-lg hover:shadow-purple-500/20">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 via-purple-600 to-purple-700 rounded-xl flex items-center justify-center flex-shrink-0 group-hover/feature:scale-110 group-hover/feature:rotate-3 transition-all duration-300 shadow-lg group-hover/feature:shadow-purple-500/40">
                    <BarChart3 className="w-8 h-8 text-white group-hover/feature:scale-110 transition-transform duration-300" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white mb-3 group-hover/feature:text-purple-100 transition-colors duration-300">Dados em dashboard</h3>
                    <p className="text-sm text-gray-400 mb-4 group-hover/feature:text-gray-300 transition-colors duration-300 leading-relaxed">
                      O centro de comando da clareza, onde o caos é domado em sinfonias visuais irresistíveis. A transformação de números brutos em uma vista panorâmica de compreensão estratégica.
                    </p>
                    <div className="flex space-x-2">
                      <div className="w-4 h-4 bg-purple-400 rounded-sm animate-pulse group-hover/feature:bg-purple-300 transition-colors duration-300"></div>
                      <div className="w-4 h-4 bg-purple-400 rounded-sm animate-pulse group-hover/feature:bg-purple-300 transition-colors duration-300" style={{animationDelay: '0.2s'}}></div>
                      <div className="w-4 h-4 bg-purple-400 rounded-sm animate-pulse group-hover/feature:bg-purple-300 transition-colors duration-300" style={{animationDelay: '0.4s'}}></div>
                      <div className="w-4 h-4 bg-purple-400 rounded-sm animate-pulse group-hover/feature:bg-purple-300 transition-colors duration-300" style={{animationDelay: '0.6s'}}></div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-6 py-6 group/feature hover:bg-white/10 rounded-xl transition-all duration-300 border border-transparent hover:border-orange-500/30 hover:shadow-lg hover:shadow-orange-500/20">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 rounded-xl flex items-center justify-center flex-shrink-0 group-hover/feature:scale-110 group-hover/feature:rotate-3 transition-all duration-300 shadow-lg group-hover/feature:shadow-orange-500/40">
                    <Zap className="w-8 h-8 text-white group-hover/feature:scale-110 transition-transform duration-300" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white mb-3 group-hover/feature:text-orange-100 transition-colors duration-300">Integrações API</h3>
                    <p className="text-sm text-gray-400 mb-4 group-hover/feature:text-gray-300 transition-colors duration-300 leading-relaxed">
                      O pulso eletrizante que conecta mundos. A corrente invisível, mas inegavelmente potente, que flui entre aplicações, permitindo que conversem e colaborem com fluidez hipnotizante.
                    </p>
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 bg-orange-400 rounded-full animate-ping group-hover/feature:bg-orange-300 transition-colors duration-300"></div>
                      <div className="w-3 h-3 bg-orange-400 rounded-full animate-ping group-hover/feature:bg-orange-300 transition-colors duration-300" style={{animationDelay: '0.3s'}}></div>
                      <div className="w-3 h-3 bg-orange-400 rounded-full animate-ping group-hover/feature:bg-orange-300 transition-colors duration-300" style={{animationDelay: '0.6s'}}></div>
                      <div className="w-3 h-3 bg-orange-400 rounded-full animate-ping group-hover/feature:bg-orange-300 transition-colors duration-300" style={{animationDelay: '0.9s'}}></div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-6 py-6 group/feature hover:bg-white/10 rounded-xl transition-all duration-300 border border-transparent hover:border-teal-500/30 hover:shadow-lg hover:shadow-teal-500/20">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-500 via-teal-600 to-teal-700 rounded-xl flex items-center justify-center flex-shrink-0 group-hover/feature:scale-110 group-hover/feature:rotate-3 transition-all duration-300 shadow-lg group-hover/feature:shadow-teal-500/40">
                    <MessageCircle className="w-8 h-8 text-white group-hover/feature:scale-110 transition-transform duration-300" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white mb-3 group-hover/feature:text-teal-100 transition-colors duration-300">Assistente IA</h3>
                    <p className="text-sm text-gray-400 mb-4 group-hover/feature:text-gray-300 transition-colors duration-300 leading-relaxed">
                      A sedução da companhia inteligente. Uma mente que antecipa, compreende e responde com intuição hipnotizante. O confidante digital capaz de conversas matizadas e orientação perspicaz.
                    </p>
                    <div className="flex space-x-2">
                      <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce group-hover/feature:bg-teal-300 transition-colors duration-300"></div>
                      <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce group-hover/feature:bg-teal-300 transition-colors duration-300" style={{animationDelay: '0.1s'}}></div>
                      <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce group-hover/feature:bg-teal-300 transition-colors duration-300" style={{animationDelay: '0.2s'}}></div>
                      <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce group-hover/feature:bg-teal-300 transition-colors duration-300" style={{animationDelay: '0.3s'}}></div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Diferencial competitivo */}
            <section className="group/section hover:bg-white/5 rounded-2xl p-8 transition-all duration-500 border border-transparent hover:border-yellow-500/20">
              <div className="bg-white/5 border-l-4 border-yellow-400 pl-6 py-4 mb-8 group-hover/section:bg-white/10 group-hover/section:border-yellow-300 transition-all duration-500 rounded-r-lg">
                <p className="text-gray-300 leading-relaxed group-hover/section:text-gray-200 transition-colors duration-500">
                  Enquanto a maioria ainda acha que precisa trabalhar mais de 40 horas por semana, outros escalam de forma automatizada e fazem muito mais dinheiro, <span className="text-yellow-400 font-semibold group-hover/section:text-yellow-300 group-hover/section:scale-105 transition-all duration-300 inline-block">TRABALHANDO MENOS.</span>
                </p>
              </div>
              
              <h2 className="text-xl font-light mb-4 text-yellow-400 group-hover/section:text-yellow-300 group-hover/section:scale-105 transition-all duration-500">
                <span className="inline-block group-hover/section:rotate-3 transition-transform duration-300">⚡</span> Porque o futuro não pertence a quem trabalha mais.
              </h2>
              <h2 className="text-xl font-light mb-6 text-blue-400 group-hover/section:text-blue-300 group-hover/section:scale-105 transition-all duration-500">
                <span className="inline-block group-hover/section:rotate-3 transition-transform duration-300">🧠</span> Pertence a quem trabalha com inteligência.
              </h2>
            </section>

            {/* Propósito */}
            <section className="group/section hover:bg-white/5 rounded-2xl p-8 transition-all duration-500 border border-transparent hover:border-blue-500/20">
              <p className="text-gray-300 mb-6 leading-relaxed group-hover/section:text-gray-200 transition-colors duration-500">
                Não era pra ser papel de nenhum advogado ficar o dia revisando documentos gigantes e escrevendo por horas..
              </p>
              <p className="text-lg text-blue-400 font-medium mb-8 leading-relaxed group-hover/section:text-blue-300 group-hover/section:scale-105 transition-all duration-500">
                <span className="inline-block group-hover/section:rotate-3 transition-transform duration-300">⚖️</span> O papel do advogado deve ser: criar estratégia, gerar valor cliente, fortalecer os laços e ter tempo livre para fazer o escritório crescer!
              </p>
              
              <div className="bg-white/5 border border-white/10 rounded-lg p-6 group-hover/section:bg-white/10 group-hover/section:border-blue-400/30 group-hover/section:shadow-lg group-hover/section:shadow-blue-500/20 transition-all duration-500">
                <p className="text-gray-300 mb-3 group-hover/section:text-gray-200 transition-colors duration-300">
                  A era da IA não está chegando.
                </p>
                <p className="text-xl font-medium text-blue-400 mb-4 group-hover/section:text-blue-300 group-hover/section:scale-105 transition-all duration-300">
                  <span className="inline-block group-hover/section:rotate-3 transition-transform duration-300">🚀</span> Ela já começou.
                </p>
                <p className="text-gray-300 leading-relaxed group-hover/section:text-gray-200 transition-colors duration-300">
                  E o seu escritório pode ser um dos primeiros a operar nesse novo patamar — com processos automatizados, decisões guiadas por dados e tempo livre para o que realmente move o seu propósito.
                </p>
              </div>
            </section>

            {/* Nossa Proposta */}
            <section className="group/section hover:bg-white/5 rounded-2xl p-8 transition-all duration-500 border border-transparent hover:border-green-500/20">
              <h2 className="text-2xl font-light mb-6 text-green-400 group-hover/section:text-green-300 group-hover/section:scale-105 transition-all duration-500">
                <span className="inline-block group-hover/section:rotate-3 transition-transform duration-300">💡</span> Nossa Proposta:
              </h2>
              <p className="text-gray-300 mb-8 leading-relaxed group-hover/section:text-gray-200 transition-colors duration-500">
                Executar um plano validado, de 3 meses, para transformação completa do escritório. Saindo de escritório tradicional para o Escritório Inteligente. Realizando em etapas:
              </p>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start space-x-4 py-4 group/step hover:bg-white/10 rounded-xl transition-all duration-300 border border-transparent hover:border-green-500/30 hover:shadow-lg hover:shadow-green-500/20">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1 group-hover/step:scale-110 group-hover/step:rotate-12 transition-all duration-300 shadow-lg">
                    <span className="text-sm font-bold text-white group-hover/step:scale-110 transition-transform duration-300">1</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-white mb-2 group-hover/step:text-green-100 transition-colors duration-300">Diagnóstico</h3>
                    <p className="text-gray-400 group-hover/step:text-gray-300 transition-colors duration-300">das rotinas atuais e peças geradas</p>
            </div>
          </div>

                <div className="flex items-start space-x-4 py-4 group/step hover:bg-white/10 rounded-xl transition-all duration-300 border border-transparent hover:border-green-500/30 hover:shadow-lg hover:shadow-green-500/20">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1 group-hover/step:scale-110 group-hover/step:rotate-12 transition-all duration-300 shadow-lg">
                    <span className="text-sm font-bold text-white group-hover/step:scale-110 transition-transform duration-300">2</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-white mb-2 group-hover/step:text-green-100 transition-colors duration-300">Mapeamento</h3>
                    <p className="text-gray-400 group-hover/step:text-gray-300 transition-colors duration-300">de automações e integrações possíveis</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 py-4 group/step hover:bg-white/10 rounded-xl transition-all duration-300 border border-transparent hover:border-green-500/30 hover:shadow-lg hover:shadow-green-500/20">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1 group-hover/step:scale-110 group-hover/step:rotate-12 transition-all duration-300 shadow-lg">
                    <span className="text-sm font-bold text-white group-hover/step:scale-110 transition-transform duration-300">3</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-white mb-2 group-hover/step:text-green-100 transition-colors duration-300">Implementação de IA</h3>
                    <p className="text-gray-400 group-hover/step:text-gray-300 transition-colors duration-300">para geração e análise de documentos</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 py-4 group/step hover:bg-white/10 rounded-xl transition-all duration-300 border border-transparent hover:border-green-500/30 hover:shadow-lg hover:shadow-green-500/20">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1 group-hover/step:scale-110 group-hover/step:rotate-12 transition-all duration-300 shadow-lg">
                    <span className="text-sm font-bold text-white group-hover/step:scale-110 transition-transform duration-300">4</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-white mb-2 group-hover/step:text-green-100 transition-colors duration-300">Criação de fluxos</h3>
                    <p className="text-gray-400 group-hover/step:text-gray-300 transition-colors duration-300">personalizados com automações entre sistemas</p>
                  </div>
            </div>
          </div>

              <div className="bg-white/5 border border-white/10 rounded-lg p-6 group-hover/section:bg-white/10 group-hover/section:border-green-400/30 group-hover/section:shadow-lg group-hover/section:shadow-green-500/20 transition-all duration-500">
                <p className="text-gray-300 leading-relaxed group-hover/section:text-gray-200 transition-colors duration-300">
                  Tudo adaptado à sua estrutura, à sua equipe e ao seu modelo de operação.
                </p>
              </div>
            </section>

            {/* Preços */}
            <section className="group/section hover:bg-white/5 rounded-2xl p-8 transition-all duration-500 border border-transparent hover:border-yellow-500/20">
              <h2 className="text-2xl font-light mb-8 text-yellow-400 group-hover/section:text-yellow-300 group-hover/section:scale-105 transition-all duration-500">
                <span className="inline-block group-hover/section:rotate-3 transition-transform duration-300">💰</span> Implementação (pagamento único):
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white/5 border border-green-500/30 rounded-lg p-6 group-hover/section:bg-white/10 group-hover/section:border-green-400/50 group-hover/section:shadow-lg group-hover/section:shadow-green-500/20 transition-all duration-500 hover:scale-105">
                  <h3 className="text-lg font-medium mb-3 text-green-400 group-hover/section:text-green-300 transition-colors duration-300">À Vista (PIX)</h3>
                  <div className="text-3xl font-light text-white mb-2 group-hover/section:text-green-100 transition-colors duration-300">{formatCurrency(params.precoPix)}</div>
                  <p className="text-sm text-gray-400 group-hover/section:text-gray-300 transition-colors duration-300">Pagamento único</p>
                </div>
                
                <div className="bg-white/5 border border-blue-500/30 rounded-lg p-6 group-hover/section:bg-white/10 group-hover/section:border-blue-400/50 group-hover/section:shadow-lg group-hover/section:shadow-blue-500/20 transition-all duration-500 hover:scale-105">
                  <h3 className="text-lg font-medium mb-3 text-blue-400 group-hover/section:text-blue-300 transition-colors duration-300">Parcelado</h3>
                  <div className="text-3xl font-light text-white mb-2 group-hover/section:text-blue-100 transition-colors duration-300">{formatCurrency(params.precoParcelado)}</div>
                  <p className="text-sm text-gray-400 group-hover/section:text-gray-300 transition-colors duration-300">Dividido em até {params.parcelas} vezes</p>
                </div>
              </div>
              
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 group-hover/section:bg-yellow-500/20 group-hover/section:border-yellow-400/50 group-hover/section:shadow-lg group-hover/section:shadow-yellow-500/20 transition-all duration-500">
                <p className="text-sm text-yellow-300 font-medium group-hover/section:text-yellow-200 transition-colors duration-300">
                  <span className="inline-block group-hover/section:rotate-3 transition-transform duration-300">⚠️</span> OBS: {params.mensalidade}
                </p>
              </div>
            </section>

            {/* CTA */}
            <section className="group/section hover:bg-white/5 rounded-2xl p-8 transition-all duration-500 border border-transparent hover:border-purple-500/20">
              <div className="bg-white/5 border border-white/10 rounded-lg p-8 text-center group-hover/section:bg-white/10 group-hover/section:border-purple-400/30 group-hover/section:shadow-lg group-hover/section:shadow-purple-500/20 transition-all duration-500">
                <h2 className="text-xl font-light mb-4 text-white group-hover/section:text-purple-100 group-hover/section:scale-105 transition-all duration-500">
                  <span className="inline-block group-hover/section:rotate-3 transition-transform duration-300">🚀</span> {params.nome ? `Pronto para transformar seu escritório, ${params.nome}?` : 'Pronto para transformar seu escritório?'}
            </h2>
                <p className="text-gray-300 mb-6 leading-relaxed group-hover/section:text-gray-200 transition-colors duration-500">
                  Entre em contato e vamos começar sua jornada para o Escritório Inteligente
            </p>
            
                {params.contato ? (
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                      href={`https://api.whatsapp.com/send/?phone=55${params.contato}&text=Olá! Gostei da proposta do Escritório Inteligente e gostaria de agendar uma reunião para discutir os detalhes.&type=phone_number&app_absent=0`}
                target="_blank" 
                rel="noopener noreferrer"
                      className="inline-block bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors duration-300 shadow-lg border border-blue-500/30"
                    >
                      💬 Quero Transformar Meu Escritório
                    </a>
                    
                    <div className="flex items-center gap-3 bg-white/10 rounded-lg px-4 py-3 border border-white/20 group-hover/section:bg-white/15 group-hover/section:border-white/30 transition-all duration-300">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center group-hover/section:scale-110 transition-transform duration-300">
                        <span className="text-white text-sm">📱</span>
            </div>
                      <div className="text-left">
                        <p className="text-xs text-gray-400 group-hover/section:text-gray-300 transition-colors duration-300">Ou ligue diretamente:</p>
                        <p className="text-lg font-bold text-white group-hover/section:text-green-300 transition-colors duration-300">
                          {formatPhoneNumber(params.contato)}
                        </p>
                </div>
                </div>
                </div>
                ) : (
                  <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300 shadow-lg">
                    💬 Quero Transformar Meu Escritório
                  </button>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}